import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { cancelSubscription } from '../services/stripeService';

const SubscriptionManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const { 
    userPlan, 
    isPro, 
    isStandard, 
    isCardOnly, 
    isTrialActive, 
    isExpired, 
    trialEndsAt, 
    planExpiresAt, 
    subscriptionStatus,
    planLimits, 
    cardCount, 
    invoiceCount, 
    monthlyInvoiceCount,
    isLoading 
  } = useSubscription();

  // 디버깅 정보
  console.log('🔍 [SubscriptionManagementPage] Component rendered');
  console.log('🔍 [SubscriptionManagementPage] Current URL:', window.location.href);
  console.log('🔍 [SubscriptionManagementPage] Location pathname:', location.pathname);
  console.log('🔍 [SubscriptionManagementPage] User:', user);
  console.log('🔍 [SubscriptionManagementPage] Component name:', 'SubscriptionManagementPage');
  console.log('🔍 [SubscriptionManagementPage] Subscription context data:', {
    userPlan,
    planExpiresAt,
    trialEndsAt,
    isExpired,
    isTrialActive,
    subscriptionStatus
  });

  // 라우팅 확인 (디버깅용)
  useEffect(() => {
    console.log('🔍 [SubscriptionManagementPage] Component mounted');
    console.log('🔍 [SubscriptionManagementPage] location.pathname:', location.pathname);
    console.log('🔍 [SubscriptionManagementPage] window.location.pathname:', window.location.pathname);
    console.log('✅ [SubscriptionManagementPage] Route is correct');
  }, []); // 빈 의존성 배열로 한 번만 실행

  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [refundHistory, setRefundHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  
  // 로그아웃 핸들러
  const handleLogout = async () => {
    try {
      setLoading(true);
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 구독 정보 로드
  useEffect(() => {
    const loadSubscriptionInfo = async () => {
      if (!user) return;
      
      try {
        console.log('🔄 [SubscriptionManagementPage] Loading subscription info for user:', user.uid);
        
        // 실제 구독 정보를 Firestore에서 가져오기
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase/config');
        
        // 올바른 경로: users/{userId}/subscription/status
        const subscriptionRef = doc(db, 'users', user.uid, 'subscription', 'status');
        console.log('🔍 [SubscriptionManagementPage] Firestore path:', `users/${user.uid}/subscription/status`);
        
        const subscriptionDoc = await getDoc(subscriptionRef);
        
        if (subscriptionDoc.exists()) {
          const subscriptionData = subscriptionDoc.data();
          console.log('📄 [SubscriptionManagementPage] Raw subscription data:', subscriptionData);
          
          const stripeSubscriptionId = subscriptionData.stripeSubscriptionId;
          const subscriptionStatus = subscriptionData.subscriptionStatus;
          const plan = subscriptionData.plan;
          
          console.log('🔍 [SubscriptionManagementPage] Parsed subscription data:', {
            stripeSubscriptionId,
            subscriptionStatus,
            plan,
            userPlan,
            isExpired,
            isTrialActive,
            planExpiresAt: subscriptionData.planExpiresAt,
            planExpiresAtDate: subscriptionData.planExpiresAt ? new Date(subscriptionData.planExpiresAt.seconds * 1000) : null
          });
          
          // 구독 취소 가능 여부 판단 (Card Only 구독도 취소 가능하도록 수정)
          const hasActiveSubscription = 
            subscriptionData.stripeSubscriptionId && 
            (subscriptionData.subscriptionStatus === 'active' || 
             (subscriptionData.plan === 'card_only' && subscriptionData.subscriptionStatus === 'canceled')) && 
            !isExpired;
          
          console.log('🔍 [SubscriptionManagementPage] Subscription validation:', {
            hasStripeId: !!subscriptionData.stripeSubscriptionId,
            status: subscriptionData.subscriptionStatus,
            isExpired,
            isTrialActive,
            plan: subscriptionData.plan,
            canCancel: hasActiveSubscription
          });
          
          if (hasActiveSubscription) {
            console.log('✅ [SubscriptionManagementPage] Setting subscriptionId to:', subscriptionData.stripeSubscriptionId);
            setSubscriptionId(subscriptionData.stripeSubscriptionId);
          } else {
            console.log('⚠️ [SubscriptionManagementPage] Cannot cancel subscription - setting to null');
            setSubscriptionId(null);
          }
        } else {
          console.log('❌ [SubscriptionManagementPage] No subscription document found at path:', `users/${user.uid}/subscription/status`);
          setSubscriptionId(null);
        }
      } catch (error) {
        console.error('❌ [SubscriptionManagementPage] Error loading subscription info:', error);
        setSubscriptionId(null);
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptionInfo();
  }, [user, userPlan, isExpired, isTrialActive]);

  // 결제 내역 로드
  const loadPaymentHistory = useCallback(async () => {
    try {
      const userId = user?.uid;
      if (!userId) return;
      
      console.log('🔄 [SubscriptionManagementPage] Loading payment history for user:', userId);
      
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      
      const subscriptionRef = doc(db, 'users', userId, 'subscription', 'status');
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data();
        console.log('🔍 [SubscriptionManagementPage] Raw subscription data:', subscriptionData);
        
        const history = Array.isArray(subscriptionData.paymentHistory) 
          ? subscriptionData.paymentHistory 
          : [];
        
        console.log('🔍 [SubscriptionManagementPage] Payment history raw data:', history);
        console.log('🔍 [SubscriptionManagementPage] Payment history type:', typeof history, 'isArray:', Array.isArray(history));
        
        // 🆕 최근 50개만 로드하여 메모리 사용량 제한 및 최신순 정렬
        const limitedHistory = history.slice(-50).reverse();
        console.log(`📊 [SubscriptionManagementPage] Loaded ${limitedHistory.length} payment records (limited from ${history.length} total)`);
        console.log('📊 [SubscriptionManagementPage] Limited history:', limitedHistory);
        setPaymentHistory(limitedHistory);
      } else {
        console.log('📄 [SubscriptionManagementPage] No subscription document found for payment history');
        setPaymentHistory([]);
      }
    } catch (error) {
      console.error('❌ [SubscriptionManagementPage] Error loading payment history:', error);
      setPaymentHistory([]);
    }
  }, [user]);

  // 환불 내역 로드
  const loadRefundHistory = useCallback(async () => {
    try {
      const userId = user?.uid;
      if (!userId) return;
      
      console.log('🔄 [SubscriptionManagementPage] Loading refund history for user:', userId);
      
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      
      const subscriptionRef = doc(db, 'users', userId, 'subscription', 'status');
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data();
        const history = subscriptionData.refundHistory || [];
        // 🆕 최신순 정렬
        setRefundHistory(history.slice().reverse());
        console.log('✅ [SubscriptionManagementPage] Refund history loaded:', history.length, 'refunds');
      } else {
        console.log('📄 [SubscriptionManagementPage] No subscription document found for refund history');
        setRefundHistory([]);
      }
    } catch (error) {
      console.error('❌ [SubscriptionManagementPage] Error loading refund history:', error);
      setRefundHistory([]);
    }
  }, [user]);

  // 화면 로드 시 데이터 로드
  useEffect(() => {
    loadPaymentHistory();
    loadRefundHistory();
  }, [loadPaymentHistory, loadRefundHistory]);

  // 🆕 실시간 결제/환불 내역 업데이트 리스너
  useEffect(() => {
    if (!user) return;
    
    console.log('🔄 [SubscriptionManagementPage] 실시간 결제/환불 내역 리스너 설정');
    
    const setupRealtimeListener = async () => {
      const { doc, onSnapshot } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      
      const subscriptionRef = doc(db, 'users', user.uid, 'subscription', 'status');
      const unsubscribe = onSnapshot(subscriptionRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          console.log('🔄 [SubscriptionManagementPage] 실시간 데이터 업데이트:', {
            paymentHistoryLength: data?.paymentHistory?.length || 0,
            refundHistoryLength: data?.refundHistory?.length || 0
          });
          
          // 실시간으로 결제/환불 내역 업데이트
          const paymentHistory = Array.isArray(data?.paymentHistory) ? data.paymentHistory : [];
          const refundHistory = Array.isArray(data?.refundHistory) ? data.refundHistory : [];
          
          const limitedPaymentHistory = paymentHistory.slice(-50).reverse();
          const reversedRefundHistory = refundHistory.slice().reverse();
          
          setPaymentHistory(limitedPaymentHistory);
          setRefundHistory(reversedRefundHistory);
          
          console.log('✅ [SubscriptionManagementPage] 실시간 데이터 업데이트 완료');
        }
      });
      
      return unsubscribe;
    };
    
    let unsubscribe: (() => void) | null = null;
    
    setupRealtimeListener().then((unsub) => {
      unsubscribe = unsub;
    });
    
    return () => {
      console.log('🧹 [SubscriptionManagementPage] 실시간 리스너 정리');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  // 결제 내역 포맷팅 함수 (앱 코드와 동일)
  const formatPaymentAmount = (amountCents?: number, priceMicros?: number) => {
    const cents = typeof amountCents === 'number'
      ? amountCents
      : (typeof priceMicros === 'number' ? Math.round(priceMicros / 10000) : 0);
    return `US$${(cents / 100).toFixed(2)}`;
  };

  const formatPaymentDate = (timestamp: any) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // 플랜 변경
  const handleChangePlan = () => {
    navigate('/paywall', { 
      state: { 
        limitType: 'plan_change',
        currentPlan: userPlan,
        isExpired: isExpired,
        fromScreen: 'SubscriptionManagement'
      }
    });
  };

  // 환불 문의 메일 보내기
  const handleRefundInquiry = (paymentIntentId: string, amount?: number, planName?: string) => {
    if (!user) {
      alert(t('common.loginRequired', '로그인이 필요합니다.'));
      return;
    }

    const subject = encodeURIComponent(t('subscription.refundEmailSubject', 'Refund Request'));
    const body = encodeURIComponent(
      t('subscription.refundEmailBody', 
        `Hello,

I would like to request a refund for my subscription.

User Email: ${user.email || 'N/A'}
User ID: ${user.uid}
Payment ID: ${paymentIntentId}
Plan: ${planName || 'N/A'}
Amount: ${amount || 'N/A'}

Please process my refund request.

Thank you.`)
    );

    const mailtoUrl = `mailto:support@scanvcf.com?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, '_blank');
  };

  // 🆕 WooCommerce 방식 참고한 구독 취소 (자동 리펀드 포함)
  const [isCancelling, setIsCancelling] = useState(false);
  const isCancellingRef = useRef(false);
  
  const handleCancelSubscription = async () => {
    console.log('🔍 [SubscriptionManagementPage] handleCancelSubscription called:', {
      subscriptionId,
      userPlan,
      isExpired,
      isTrialActive,
      user: user?.uid,
      timestamp: new Date().toISOString(),
      isCancelling: isCancelling,
      isCancellingRef: isCancellingRef.current
    });
    
    if (isCancelling || isCancellingRef.current) {
      console.log('⚠️ [SubscriptionManagementPage] Cancellation already in progress, ignoring duplicate call');
      return;
    }
    
    if (!user) {
      console.error('❌ [SubscriptionManagementPage] No user found');
      alert(t('common.loginRequired', '로그인이 필요합니다.'));
      return;
    }

    if (!subscriptionId) {
      console.error('❌ [SubscriptionManagementPage] No subscriptionId found:', subscriptionId);
      alert(t('subscription.noActiveSubscription', '활성 구독이 없습니다.'));
      return;
    }

    console.log('🔄 [SubscriptionManagementPage] Showing confirmation dialog...');
    const confirmed = window.confirm(
      t('subscription.cancelSubscriptionDesc', '구독을 취소하시겠습니까? 취소 시 자동으로 리펀드가 처리됩니다.')
    );

    console.log('🔍 [SubscriptionManagementPage] User confirmation result:', confirmed);
    if (!confirmed) return;

    setIsCancelling(true);
    isCancellingRef.current = true;
    
    try {
      console.log('🔄 [SubscriptionManagementPage] 구독 취소 및 자동 리펀드 시작 - subscriptionId:', subscriptionId);
      
      // 🆕 WooCommerce 방식: 구독 취소와 동시에 자동 리펀드 처리
      const result = await cancelSubscription(subscriptionId);
      
      if (result.success) {
        console.log('✅ [SubscriptionManagementPage] 구독 취소 및 리펀드 성공:', result);
        alert(t('subscription.cancellationAndRefundSuccess', '구독이 취소되었고 리펀드가 자동으로 처리되었습니다.'));
        
        // 🆕 관리자에게 구독 취소 및 리펀드 완료 알림 메일
        const emailSubject = encodeURIComponent('[ScanVCF] 구독 취소 및 리펀드 완료');
        const emailBody = encodeURIComponent(
          `구독 취소 및 리펀드가 자동으로 처리되었습니다.

=== 고객 정보 ===
고객 이메일: ${user.email || 'N/A'}
사용자 ID: ${user.uid}
구독 ID: ${subscriptionId}

=== 처리 정보 ===
취소 시간: ${new Date().toLocaleString('ko-KR')}
취소 사유: 고객 요청
리펀드 상태: 자동 처리 완료
처리 방식: WooCommerce Stripe 방식 적용

=== 확인 요청 ===
스트라이프 대시보드에서 리펀드가 정상적으로 처리되었는지 확인해주세요.

감사합니다.`
        );
        
        const mailtoUrl = `mailto:support@scanvcf.com?subject=${emailSubject}&body=${emailBody}`;
        window.open(mailtoUrl, '_blank');
        
        // 즉시 UI 상태 업데이트
        console.log('🔄 [SubscriptionManagementPage] 즉시 UI 상태 업데이트');
        setSubscriptionId(null); // 취소 버튼 숨기기
        setLoading(true); // 로딩 상태로 변경
        
        // 🆕 실시간 데이터 새로고침 (리펀드 히스토리 포함)
        console.log('🔄 [SubscriptionManagementPage] 데이터 새로고침 시작');
        
        // 즉시 데이터 다시 로드
        setTimeout(() => {
          loadPaymentHistory();
          loadRefundHistory();
          console.log('🔄 [SubscriptionManagementPage] 데이터 새로고침 완료');
        }, 1000); // 1초 후 데이터 새로고침
        
      } else if (result.status === 'already_canceled') {
        console.log('⚠️ [SubscriptionManagementPage] 구독이 이미 취소됨:', result);
        alert(t('subscription.alreadyCanceled', '구독이 이미 취소되었습니다.'));
        
        // 🆕 이미 취소된 경우에도 UI 상태 업데이트
        console.log('🔄 [SubscriptionManagementPage] 이미 취소된 구독 - UI 상태 업데이트');
        setSubscriptionId(null); // 취소 버튼 숨기기
        
        // 🆕 데이터 새로고침
        setTimeout(() => {
          console.log('🔄 [SubscriptionManagementPage] 데이터 새로고침 시작');
          loadPaymentHistory();
          loadRefundHistory();
          console.log('🔄 [SubscriptionManagementPage] 데이터 새로고침 완료');
        }, 1000);
        
      } else {
        alert(t('subscription.cancellationError', '구독 취소에 실패했습니다.'));
      }
    } catch (error) {
      console.error('Cancel subscription error:', error);
      alert(t('subscription.cancellationError', '구독 취소 중 오류가 발생했습니다.'));
    } finally {
      setIsCancelling(false);
      isCancellingRef.current = false;
    }
  };

  // 상태 텍스트 가져오기
  const getStatusText = () => {
    if (isExpired) return t('subscription.expired.title', '구독 만료');
    if (isTrialActive) return t('subscription.plans.freeTrialName', '무료 체험');
    if (isCardOnly) return t('subscription.cardOnly', 'Card Only');
    if (isStandard) return t('subscription.plans.standard', '스탠다드');
    if (isPro) return t('subscription.plans.pro', '프로');
    return t('subscription.unknown', '알 수 없음');
  };

  // 상태 색상 가져오기
  const getStatusColor = () => {
    if (isExpired) return 'bg-red-100 text-red-800';
    if (isTrialActive) return 'bg-gray-100 text-gray-800';
    if (isCardOnly) return 'bg-[#c8a45e]/15 text-[#96742f]';
    if (isStandard) return 'bg-[#c8a45e]/15 text-[#96742f]';
    if (isPro) return 'bg-[#0a0e1a] text-[#dcc08a]';
    return 'bg-gray-100 text-gray-800';
  };

  // 만료일 텍스트 가져오기
  const getExpirationText = () => {
    console.log('🔍 [SubscriptionManagementPage] getExpirationText called:', {
      isTrialActive,
      trialEndsAt,
      planExpiresAt,
      userPlan
    });
    
    if (isTrialActive && trialEndsAt) {
      const text = t('subscription.trialEndsAt', '무료 체험 종료') + ': ' + trialEndsAt.toLocaleDateString();
      console.log('🔍 [SubscriptionManagementPage] Trial expiration text:', text);
      return text;
    }
    if (planExpiresAt) {
      const text = t('subscription.planExpiresAt', '구독 만료') + ': ' + planExpiresAt.toLocaleDateString();
      console.log('🔍 [SubscriptionManagementPage] Plan expiration text:', text);
      return text;
    }
    console.log('🔍 [SubscriptionManagementPage] No expiration text available');
    return '';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading', '로딩 중...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* 현재 구독 상태 */}
          <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                📊 {t('subscription.currentStatus', '현재 구독 상태')}
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()}`}>
                    {getStatusText()}
                  </span>
                  
                  {!isExpired && (
                    <p className="text-sm text-gray-600">{getExpirationText()}</p>
                  )}
                </div>

                {/* 사용량 표시 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-sm font-medium text-gray-500">{t('common.businessCard', '명함')}</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {cardCount} / {planLimits.maxCards === -1 ? '∞' : planLimits.maxCards}
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-sm font-medium text-gray-500">{t('invoice.title')}</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {invoiceCount} / {planLimits.maxInvoicesPerYear === -1 ? '∞' : planLimits.maxInvoicesPerYear}
                    </div>
                  </div>
                  
                  {monthlyInvoiceCount > 0 && (
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-sm font-medium text-gray-500">{t('subscription.monthly', '월간')}</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {monthlyInvoiceCount} / {planLimits.maxInvoicesPerMonth === -1 ? '∞' : planLimits.maxInvoicesPerMonth}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 플랜 관리 */}
          <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                🔄 {t('subscription.planManagement', '플랜 관리')}
              </h3>
              
              <div className="space-y-4">
                <button
                  onClick={handleChangePlan}
                  className="lux-btn-gold w-full flex items-center justify-center px-4 py-3 text-sm font-semibold rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c8a45e]"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  {t('subscription.upgrade', '플랜 변경')}
                </button>
                
                {/* 구독 취소 버튼 - Stripe 구독이 있을 때만 표시 */}
                {(() => {
                  console.log('🔍 [SubscriptionManagementPage] Cancel button render check:', {
                    isExpired,
                    isTrialActive,
                    subscriptionId,
                    shouldShow: !isExpired && !isTrialActive && subscriptionId
                  });
                  return null;
                })()}
                {!isExpired && !isTrialActive && subscriptionId && (
                  <button
                    onClick={() => {
                      console.log('🔍 [SubscriptionManagementPage] Cancel button clicked!');
                      handleCancelSubscription();
                    }}
                    disabled={isCancelling}
                    className={`w-full flex items-center justify-center px-4 py-3 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                      isCancelling 
                        ? 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed' 
                        : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    {isCancelling ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('subscription.cancelling', '취소 중...')}
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {t('subscription.cancelSubscription', '구독 취소')}
                      </>
                    )}
                  </button>
                )}
                
                {/* 명함 전용 플랜 안내 메시지 */}
                {!isExpired && !isTrialActive && userPlan === 'CARD_ONLY' && (
                  <div className="w-full flex items-center justify-center px-4 py-3 border border-gray-200 text-sm font-medium rounded-md text-gray-500 bg-gray-50">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('subscription.cardOnlyNoCancel', 'Card Only plan cannot be canceled.')}
                  </div>
                )}
                
                {/* 활성 구독이 없을 때 메시지 */}
                {!isExpired && !isTrialActive && !subscriptionId && userPlan === 'FREE_TRIAL' && (
                  <div className="w-full flex items-center justify-center px-4 py-3 border border-gray-200 text-sm font-medium rounded-md text-gray-400 bg-gray-50">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('subscription.noActiveSubscription', '활성 구독이 없습니다')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 결제 내역 */}
          <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                💳 {t('subscription.paymentHistory', '결제 내역')}
              </h3>
              
              {paymentHistory.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">{t('subscription.noPaymentHistory', '결제 내역이 없습니다.')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentHistory.map((payment, index) => (
                    <div key={payment.paymentIntentId || payment.purchaseToken || `payment-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {payment.planType || payment.plan || t('subscription.unknown', '알 수 없음')}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatPaymentDate(payment.purchaseTime || payment.createdAt || payment.date)}
                        </p>
                        {payment.productId && (
                          <p className="text-xs text-gray-400">
                            {payment.productId}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {formatPaymentAmount(payment.amount, payment.priceMicros)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {payment.status === 'succeeded' || payment.purchaseState === 0 || payment.acknowledged 
                              ? t('subscription.completed', '완료') 
                              : payment.status || t('subscription.unknown', '알 수 없음')
                            }
                          </p>
                        </div>
                        {(payment.status === 'completed' || payment.status === 'succeeded' || payment.purchaseState === 0) && 
                         (payment.paymentIntentId || payment.purchaseToken) && (
                          <button
                            onClick={() => handleRefundInquiry(
                              payment.paymentIntentId || payment.purchaseToken, 
                              payment.amount || payment.priceMicros, 
                              payment.planType || payment.plan
                            )}
                            className="px-3 py-1 text-xs font-semibold text-[#96742f] bg-[#c8a45e]/15 rounded-full hover:bg-[#c8a45e]/25 transition-colors"
                          >
                            {t('subscription.contactSupport', '문의하기')}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 환불 내역 */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                🔄 {t('subscription.refundHistory', '환불 내역')}
              </h3>
              
              {refundHistory.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">{t('subscription.noRefundHistory', '환불 내역이 없습니다.')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {refundHistory.map((refund, index) => (
                    <div key={refund.refundId || `refund-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {refund.planType || refund.plan || t('subscription.unknown', '알 수 없음')}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatPaymentDate(refund.refundedAt || refund.createdAt || refund.date)}
                        </p>
                        {(refund.refundReason || refund.reason) && (
                          <p className="text-xs text-gray-400 mt-1 italic">
                            {t('subscription.refundReason', '환불 사유')}: {refund.refundReason || refund.reason}
                          </p>
                        )}
                        {refund.refundId && (
                          <p className="text-xs text-gray-400 mt-1">
                            ID: {refund.refundId}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-red-600">
                          -{formatPaymentAmount(refund.refundAmount || refund.amount, refund.refundPriceMicros)}
                        </p>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          refund.status === 'succeeded' || refund.status === 'refunded' ? 'bg-green-100 text-green-800' : 
                          refund.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'
                        }`}>
                          {refund.status === 'succeeded' || refund.status === 'refunded' ? t('subscription.refundCompleted', '환불 완료') :
                           refund.status === 'pending' ? t('subscription.refundPending', '환불 대기') :
                           t('subscription.refundFailed', '환불 실패')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagementPage;
